import { Server, ShieldCheck, Globe } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { EmptyState, Skeleton } from '../../ui';
import { useQuery } from '../../gql/hooks';
import {
  DOCS_PROVIDERS_QUERY,
  type DocsProvidersQueryResult,
} from '../../gql/operations';
import { type Provider } from '../../gql/types';
import { HealthStateBadge } from '../shared/StatusBadge';
import { InlineBanner } from '../shared/InlineBanner';
import { providerLabel } from '../shared/providerLabels';
import { isResidencySafe, residencyLabel } from './residency';

// LIVE "models & providers" availability panel (Ticket W4 — Surface 2). Queries `docsProviders`
// (providers WITH their modelAliases) so a consumer can see, right now: which providers exist,
// their type/residency/enabled/health, and the canonical-model → provider-model alias table — i.e.
// exactly what canonical names are callable and where each would run.

function ResidencyChip({ provider }: { provider: Provider }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const safe = isResidencySafe(provider.type);
  const label = residencyLabel(provider.type, provider.dataResidency);
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[10px] font-medium uppercase tracking-widest ${
        safe
          ? isDark
            ? 'border-emerald-500/20 bg-emerald-500/10 text-emerald-300'
            : 'border-emerald-200 bg-emerald-50 text-emerald-700'
          : isDark
            ? 'border-white/[0.1] bg-white/[0.04] text-white/60'
            : 'border-slate-200 bg-slate-100 text-slate-600'
      }`}
    >
      {safe ? (
        <ShieldCheck className="h-3 w-3" aria-hidden="true" />
      ) : (
        <Globe className="h-3 w-3" aria-hidden="true" />
      )}
      {label}
    </span>
  );
}

function AliasTable({ provider }: { provider: Provider }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  if (provider.modelAliases.length === 0) {
    return (
      <p className={`mt-3 text-xs ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
        No model aliases configured — nothing is callable on this provider.
      </p>
    );
  }

  return (
    <table className="mt-4 w-full border-collapse text-left text-sm">
      <caption className="sr-only">
        Canonical model to provider model aliases for {provider.displayName}
      </caption>
      <thead>
        <tr>
          <th
            scope="col"
            className={`pb-2 text-[10px] font-semibold uppercase tracking-widest ${
              isDark ? 'text-white/30' : 'text-slate-400'
            }`}
          >
            Canonical model
          </th>
          <th
            scope="col"
            className={`pb-2 text-[10px] font-semibold uppercase tracking-widest ${
              isDark ? 'text-white/30' : 'text-slate-400'
            }`}
          >
            Runs as
          </th>
        </tr>
      </thead>
      <tbody>
        {provider.modelAliases.map((alias) => (
          <tr
            key={alias.id}
            className={`border-t ${isDark ? 'border-white/[0.06]' : 'border-slate-100'} ${
              alias.enabled ? '' : 'opacity-50'
            }`}
          >
            <td className="py-1.5 pr-3">
              <code
                className={`font-mono text-xs ${isDark ? 'text-indigo-200' : 'text-indigo-700'}`}
              >
                {alias.canonicalModel}
              </code>
            </td>
            <td className="py-1.5">
              <code className={`font-mono text-xs ${isDark ? 'text-white/60' : 'text-slate-600'}`}>
                {alias.providerModel}
              </code>
              {!alias.enabled && (
                <span className={`ml-2 text-[10px] ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
                  (disabled)
                </span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function AvailabilityCard({ provider }: { provider: Provider }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  return (
    <article
      aria-label={provider.displayName}
      className={`rounded-2xl border p-5 transition-all duration-300 ${
        provider.enabled ? '' : 'opacity-60'
      } ${
        isDark
          ? 'border-white/[0.06] bg-gradient-to-br from-white/[0.05] to-white/[0.02]'
          : 'border-slate-200 bg-white shadow-sm'
      }`}
    >
      <header className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span
            className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-lg`}
          >
            <Server className="h-5 w-5" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <h3
              className={`truncate text-base font-semibold ${
                isDark ? 'text-white' : 'text-slate-900'
              }`}
            >
              {provider.displayName}
            </h3>
            <p
              className={`truncate font-mono text-[11px] ${
                isDark ? 'text-white/30' : 'text-slate-400'
              }`}
            >
              {providerLabel(provider.type)}
            </p>
          </div>
        </div>
        <HealthStateBadge state={provider.health.state} />
      </header>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <ResidencyChip provider={provider} />
        <span
          className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[10px] font-medium uppercase tracking-widest ${
            provider.enabled
              ? isDark
                ? 'border-emerald-500/20 bg-emerald-500/10 text-emerald-300'
                : 'border-emerald-200 bg-emerald-50 text-emerald-700'
              : isDark
                ? 'border-white/[0.1] bg-white/[0.04] text-white/50'
                : 'border-slate-200 bg-slate-100 text-slate-500'
          }`}
        >
          {provider.enabled ? 'Enabled' : 'Disabled'}
        </span>
      </div>

      <AliasTable provider={provider} />
    </article>
  );
}

export function ModelAvailabilityPanel() {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { data, loading, error } = useQuery<DocsProvidersQueryResult>(DOCS_PROVIDERS_QUERY);
  const providers = data?.providers ?? [];

  return (
    <section aria-labelledby="availability-heading">
      <div className="mb-4">
        <h2
          id="availability-heading"
          className={`text-xl font-bold tracking-tight ${
            isDark ? 'text-white' : 'text-slate-900'
          }`}
        >
          Models &amp; providers — live availability
        </h2>
        <p className={`mt-1 text-sm ${isDark ? 'text-white/40' : 'text-slate-500'}`}>
          Every configured provider with its current health and the canonical model names callable
          right now — and the concrete provider model each would run as.
        </p>
      </div>

      {error && (
        <InlineBanner tone="error">Couldn’t load availability: {error.message}</InlineBanner>
      )}

      {loading && providers.length === 0 && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="availability-loading">
          {[0, 1].map((i) => (
            <Skeleton key={i} variant="block" height={220} />
          ))}
        </div>
      )}

      {!loading && !error && providers.length === 0 && (
        <EmptyState
          icon={<Server className="h-14 w-14" aria-hidden="true" />}
          title="No providers configured"
          description="Once providers and model aliases are configured, the callable canonical models will appear here."
        />
      )}

      {providers.length > 0 && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {providers.map((provider) => (
            <AvailabilityCard key={provider.id} provider={provider} />
          ))}
        </div>
      )}
    </section>
  );
}
